'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import Link from 'next/link';
import { usePathname } from 'next/navigation';

import {
  Check,
  Clock,
  Hospital,
  Mail,
  Sparkles,
  Stethoscope,
  Zap,
} from 'lucide-react';

import { cn } from '@kit/ui/utils';

import {
  TIERS,
  type TierDefinition,
  type TierId,
} from '~/config/subscription-tiers.config';
import type { PricingVisibility } from '~/lib/section-visibility';

import { type DedicatedPlan, DedicatedPlanCard } from './dedicated-plan-card';
import { DrugExplainer, Features } from './pricing-features';
import { PricingNotifyPopup } from './pricing-notify-popup';

/* ========================================================================== */
/*  Trial state                                                               */
/* ========================================================================== */

export type PricingTrialState =
  | { type: 'active'; daysRemaining: number }
  | { type: 'expired' }
  | { type: 'subscribed'; tier: TierId }
  | { type: 'none' };

/**
 * Trial users get Premium (medium) tier limits.
 * This determines which card shows the trial badge.
 */
const TRIAL_TIER: TierId = 'medium';

const TRIAL_DAYS = Number(process.env.NEXT_PUBLIC_TRIAL_PERIOD_DAYS) || 7;

/* ========================================================================== */
/*  Audience toggle                                                           */
/* ========================================================================== */

type Audience = 'individual' | 'organization';

function AudienceToggle({
  audience,
  onChange,
}: {
  audience: Audience;
  onChange: (a: Audience) => void;
}) {
  return (
    <div className="mb-8 flex justify-center @3xl:mb-12">
      <div className="relative inline-flex rounded-md border border-[#d5d0c8] bg-[#eceae4] p-0.5 shadow-[1px_1px_0_0_#d5d0c8]">
        <div
          className="absolute top-0.5 bottom-0.5 rounded bg-[#fffef9] shadow-sm transition-all duration-300 ease-out"
          style={{
            left: audience === 'individual' ? '2px' : '50%',
            right: audience === 'organization' ? '2px' : '50%',
          }}
        />
        <button
          type="button"
          onClick={() => onChange('individual')}
          className={cn(
            'relative z-10 flex items-center gap-1 rounded px-2.5 py-1.5 text-[11px] font-semibold whitespace-nowrap transition-colors sm:gap-1.5 sm:px-3.5 sm:text-xs',
            audience === 'individual' ? 'text-zinc-900' : 'text-slate-500',
          )}
        >
          <Stethoscope className="h-3 w-3 shrink-0" />
          Lekarze i Farmaceuci
        </button>
        <button
          type="button"
          onClick={() => onChange('organization')}
          className={cn(
            'relative z-10 flex items-center gap-1 rounded px-2.5 py-1.5 text-[11px] font-semibold whitespace-nowrap transition-colors sm:gap-1.5 sm:px-3.5 sm:text-xs',
            audience === 'organization' ? 'text-zinc-900' : 'text-slate-500',
          )}
        >
          <Hospital className="h-3 w-3 shrink-0" />
          Placówki i Organizacje
        </button>
      </div>
    </div>
  );
}

/* ========================================================================== */
/*  Organization plan                                                         */
/* ========================================================================== */

function OrgPlan({
  trialState,
}: {
  trialState: PricingTrialState;
  variant: 'landing' | 'dashboard';
}) {
  const isSubscribed = trialState.type === 'subscribed';
  const isOnTrial = trialState.type === 'active';
  const isExpiredTrial = trialState.type === 'expired';
  const daysRemaining = isOnTrial ? trialState.daysRemaining : 0;

  const cardClass = cn(
    'relative flex flex-col rounded-xl border-2 bg-[#fffef9] p-6',
    isSubscribed && 'border-green-500 shadow-[3px_3px_0_0_#22c55e]',
    isOnTrial && 'border-blue-500 shadow-[3px_3px_0_0_#3b82f6]',
    isExpiredTrial && 'border-red-400 shadow-[3px_3px_0_0_#f87171]',
    !isSubscribed &&
      !isOnTrial &&
      !isExpiredTrial &&
      'border-zinc-900 shadow-[3px_3px_0_0_#303030]',
  );

  return (
    <div className="mx-auto max-w-xs">
      <div className={cardClass}>
        {isSubscribed && (
          <span className="absolute -top-3 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1.5 rounded-full border-2 border-green-600 bg-green-600 px-3 py-0.5 text-[9px] font-semibold tracking-wider whitespace-nowrap text-white uppercase">
            <Sparkles className="h-2.5 w-2.5" />
            Twój plan
          </span>
        )}
        {isOnTrial && (
          <span className="absolute -top-3 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1.5 rounded-full border-2 border-blue-600 bg-blue-600 px-3 py-0.5 text-[9px] font-semibold tracking-wider whitespace-nowrap text-white uppercase">
            <Clock className="h-2.5 w-2.5" />
            Okres próbny
          </span>
        )}
        {isExpiredTrial && (
          <span className="absolute -top-3 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1.5 rounded-full border-2 border-red-500 bg-red-500 px-3 py-0.5 text-[9px] font-semibold tracking-wider whitespace-nowrap text-white uppercase">
            Trial wygasł
          </span>
        )}

        <h3 className="font-heading text-xl font-semibold text-slate-700">
          Organizacja
        </h3>
        <p className="font-heading mt-0.5 text-sm text-slate-500 italic">
          Apteki, przychodnie, szpitale i sieci
        </p>

        <div className="mt-3">
          <span className="text-2xl font-black text-zinc-900">
            Indywidualna wycena
          </span>
        </div>

        <div className="mt-5 border-t border-dashed border-slate-200 pt-4">
          <div className="flex flex-col gap-1.5">
            {[
              { text: 'Do 15 leków jednocześnie', highlight: true, info: true },
              {
                text: 'Elastyczne limity analiz',
                highlight: true,
                info: false,
              },
              { text: 'Wielu użytkowników', highlight: false, info: false },
            ].map((f) => (
              <div
                key={f.text}
                className="flex items-start gap-2 text-sm text-slate-600"
              >
                <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-green-300" />
                {f.highlight ? (
                  <span className="relative font-semibold text-slate-800">
                    <span className="absolute right-0 bottom-0 left-0 h-[0.4em] bg-green-300/50" />
                    <span className="relative">{f.text}</span>
                  </span>
                ) : (
                  f.text
                )}
                {f.info && <DrugExplainer drugs={15} pairs={105} />}
              </div>
            ))}
          </div>
        </div>

        <Link href="/contact" className={BTN_PRIMARY}>
          <Mail className="h-4 w-4" />
          Skontaktuj się z nami
        </Link>

        <p className="mt-3 text-center text-xs text-slate-400">
          Rozpocznij od bezpłatnego pilotażu
        </p>

        {isSubscribed && (
          <div className="-mx-6 mt-auto -mb-6 pt-6">
            <div className="rounded-b-[10px] border-t-2 border-green-200 bg-green-50 px-4 py-2.5 text-center">
              <p className="flex items-center justify-center gap-1.5 text-xs font-semibold text-green-700">
                <Sparkles className="h-3 w-3" />
                Subskrypcja aktywna
              </p>
            </div>
          </div>
        )}
        {isOnTrial && (
          <div className="-mx-6 mt-6 -mb-6 rounded-b-[10px] border-t-2 border-blue-200 bg-blue-50 px-4 py-2.5 text-center">
            <p className="flex items-center justify-center gap-1.5 text-xs font-semibold text-blue-700">
              <Clock className="h-3 w-3" />
              {daysRemaining === 1
                ? 'Ostatni dzień okresu próbnego'
                : `Pozostało ${daysRemaining} dni okresu próbnego`}
            </p>
          </div>
        )}
        {isExpiredTrial && (
          <div className="-mx-6 mt-6 -mb-6 rounded-b-[10px] border-t-2 border-red-200 bg-red-50 px-4 py-2.5 text-center">
            <p className="text-xs font-semibold text-red-600">
              Wybierz plan, aby kontynuować
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ========================================================================== */
/*  Button styles                                                             */
/* ========================================================================== */

const BTN_PRIMARY =
  'mt-6 flex whitespace-nowrap items-center justify-center gap-2 rounded-lg border-2 border-[#303030] bg-primary px-6 py-2.5 text-sm font-semibold text-white shadow-[inset_1px_1px_0_0_#303030,3px_3px_0_0_#303030] transition-all hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[inset_1px_1px_0_0_#303030,2px_2px_0_0_#303030]';

const BTN_WARM =
  'mt-6 flex whitespace-nowrap items-center justify-center gap-2 rounded-lg border-2 border-[#c8c3b8] bg-[#f7f5ef] px-6 py-2.5 text-sm font-semibold text-stone-700 shadow-[3px_3px_0_0_#c8c3b8] transition-all hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[1px_1px_0_0_#c8c3b8]';

/* ========================================================================== */
/*  Badge                                                                     */
/* ========================================================================== */

function CardBadge({
  tier,
  isOnTrial,
  isExpiredTrial,
  isSubscribed,
  showHighlighted,
}: {
  tier: TierDefinition;
  isOnTrial: boolean;
  isExpiredTrial: boolean;
  isSubscribed: boolean;
  showHighlighted: boolean;
}) {
  if (isSubscribed) {
    return (
      <span className="absolute -top-3 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1.5 rounded-full border-2 border-green-600 bg-green-600 px-3 py-0.5 text-[9px] font-semibold tracking-wider whitespace-nowrap text-white uppercase">
        <Sparkles className="h-2.5 w-2.5" />
        Twój plan
      </span>
    );
  }

  if (isOnTrial) {
    return (
      <span className="absolute -top-3 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1.5 rounded-full border-2 border-blue-600 bg-blue-600 px-3 py-0.5 text-[9px] font-semibold tracking-wider whitespace-nowrap text-white uppercase">
        <Clock className="h-2.5 w-2.5" />
        Okres próbny
      </span>
    );
  }

  if (isExpiredTrial) {
    return (
      <span className="absolute -top-3 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1.5 rounded-full border-2 border-red-500 bg-red-500 px-3 py-0.5 text-[9px] font-semibold tracking-wider whitespace-nowrap text-white uppercase">
        Trial wygasł
      </span>
    );
  }

  if (showHighlighted) {
    return (
      <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full border-2 border-zinc-900 bg-zinc-900 px-3 py-0.5 text-[9px] font-semibold tracking-wider whitespace-nowrap text-white uppercase">
        {tier.badge}
      </span>
    );
  }

  return null;
}

/* ========================================================================== */
/*  Bottom stripe                                                             */
/* ========================================================================== */

function CardStripe({
  isOnTrial,
  isExpiredTrial,
  isSubscribed,
  isLandingTrial,
  daysRemaining,
}: {
  isOnTrial: boolean;
  isExpiredTrial: boolean;
  isSubscribed: boolean;
  isLandingTrial: boolean;
  daysRemaining: number;
}) {
  if (isLandingTrial) {
    return (
      <div className="-mx-6 mt-auto -mb-6 pt-6 @3xl:-mx-7 @3xl:-mb-7">
        <div className="rounded-b-[10px] bg-[#f7f5ef] px-4 py-3 text-center">
          <p className="flex items-center justify-center gap-1.5 text-[10px] font-medium whitespace-nowrap text-emerald-600 sm:text-[11px]">
            <Zap className="h-3 w-3 shrink-0" />
            Rejestracja w 30 sek &middot; Bez karty kredytowej
          </p>
        </div>
      </div>
    );
  }

  if (isSubscribed) {
    return (
      <div className="-mx-6 mt-auto -mb-6 pt-6 @3xl:-mx-7 @3xl:-mb-7">
        <div className="rounded-b-[10px] border-t-2 border-green-200 bg-green-50 px-4 py-2.5 text-center">
          <p className="flex items-center justify-center gap-1.5 text-xs font-semibold text-green-700">
            <Sparkles className="h-3 w-3" />
            Subskrypcja aktywna
          </p>
        </div>
      </div>
    );
  }

  if (isOnTrial) {
    return (
      <div className="-mx-6 mt-6 -mb-6 rounded-b-[10px] border-t-2 border-blue-200 bg-blue-50 px-4 py-2.5 text-center @3xl:-mx-7 @3xl:-mb-7">
        <p className="flex items-center justify-center gap-1.5 text-xs font-semibold text-blue-700">
          <Clock className="h-3 w-3" />
          {daysRemaining === 1
            ? 'Ostatni dzień okresu próbnego'
            : `Pozostało ${daysRemaining} dni okresu próbnego`}
        </p>
      </div>
    );
  }

  if (isExpiredTrial) {
    return (
      <div className="-mx-6 mt-6 -mb-6 rounded-b-[10px] border-t-2 border-red-200 bg-red-50 px-4 py-2.5 text-center @3xl:-mx-7 @3xl:-mb-7">
        <p className="text-xs font-semibold text-red-600">
          Wybierz plan, aby kontynuować
        </p>
      </div>
    );
  }

  return null;
}

/* ========================================================================== */
/*  Main component                                                            */
/* ========================================================================== */

export interface PricingSectionProps {
  /** Current tier for highlighting (dashboard variant) */
  currentTier?: TierId;
  /** 'landing' = CTA links to sign-up; 'dashboard' = CTA opens notify popup */
  variant: 'landing' | 'dashboard';
  /** Trial/subscription state for dashboard variant */
  trialState?: PricingTrialState;
  /** User type from profile — auto-selects the audience tab */
  userType?: string | null;
  /** Optional title rendered inside the section */
  title?: string;
  /** Which audience tabs and tiers to show (admin page-section toggles) */
  visibility: PricingVisibility;
  /**
   * The user's dedicated plan. When set, only its card is shown — no tier
   * cards, audience tabs, organization card or notify popup.
   */
  dedicatedPlan?: DedicatedPlan;
}

const ORG_USER_TYPES = new Set([
  'pharma',
  'medical_place',
  'hospital',
  'partner',
]);

export function PricingSection({
  currentTier,
  variant,
  trialState = { type: 'none' },
  userType,
  title,
  visibility,
  dedicatedPlan,
}: PricingSectionProps) {
  const [notifyOpen, setNotifyOpen] = useState(false);
  const isOrgUser = !!(userType && ORG_USER_TYPES.has(userType));
  const [selectedAudience, setAudience] = useState<Audience>(
    isOrgUser ? 'organization' : 'individual',
  );

  // Fall back to the other tab when the preferred one is hidden
  const audience: Audience = !visibility.individual
    ? 'organization'
    : !visibility.organization
      ? 'individual'
      : selectedAudience;

  // A single contract, not an offer: no grey stage (built for comparing
  // several cards), just the card and a way to reach us about changes.
  if (dedicatedPlan) {
    return (
      <div className="w-full px-4 pb-10">
        {title && <SectionTitle title={title} />}

        <div className="mx-auto max-w-sm pt-2">
          <DedicatedPlanCard plan={dedicatedPlan} />

          <div className="mt-10 flex flex-col items-center gap-3 rounded-xl border-2 border-dashed border-[#d5d0c8] px-4 py-4 text-center">
            <p className="text-sm text-slate-600">
              Chcesz zmienić limity lub przedłużyć umowę?
            </p>
            <Link href="/contact" className={cn(BTN_WARM, 'mt-0 px-5 py-2')}>
              <Mail className="h-4 w-4" />
              Skontaktuj się z nami
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!visibility.individual && !visibility.organization) {
    return null;
  }

  // Only show trial/subscription state on the tab matching the user's type
  const orgTrialState: PricingTrialState = isOrgUser
    ? trialState
    : { type: 'none' };
  const individualTrialState: PricingTrialState = isOrgUser
    ? { type: 'none' }
    : trialState;

  // A user's own plan stays visible even when the admin hides that tier
  const ownTierId: TierId | null =
    individualTrialState.type === 'subscribed'
      ? individualTrialState.tier
      : individualTrialState.type === 'active'
        ? TRIAL_TIER
        : (currentTier ?? null);

  const tiers = TIERS.filter(
    (tier) => visibility.tiers.includes(tier.id) || tier.id === ownTierId,
  );

  return (
    <PricingShell variant={variant}>
      {title && <SectionTitle title={title} />}

      {visibility.individual && visibility.organization && (
        <AudienceToggle audience={audience} onChange={setAudience} />
      )}

      {audience === 'organization' ? (
        <OrgPlan trialState={orgTrialState} variant={variant} />
      ) : (
        <PricingCarousel
          tiers={tiers}
          variant={variant}
          trialState={individualTrialState}
          currentTier={currentTier}
          onNotify={() => setNotifyOpen(true)}
        />
      )}

      {variant === 'dashboard' && (
        <PricingNotifyPopup
          open={notifyOpen}
          onClose={() => setNotifyOpen(false)}
        />
      )}
    </PricingShell>
  );
}

function PricingShell({
  variant,
  children,
}: React.PropsWithChildren<{ variant: 'landing' | 'dashboard' }>) {
  return (
    <section
      className={cn(
        '@container w-full bg-[#f7f5ef]/60 px-4 pt-5 pb-10 @3xl:pt-6 @3xl:pb-14',
        variant === 'dashboard' &&
          'rounded-lg border pt-8 @3xl:pt-10 dark:bg-stone-900/50',
      )}
    >
      {children}
    </section>
  );
}

function SectionTitle({ title }: { title: string }) {
  return (
    <div className="mb-6 flex items-center justify-center @3xl:mb-10">
      <div className="inline-flex items-center gap-2.5">
        <span aria-hidden="true" className="h-px w-10 bg-slate-300" />
        <h2 className="font-heading relative -skew-x-3 px-2 py-0.5 text-sm font-semibold tracking-[0.08em] text-slate-700 uppercase italic @3xl:text-base">
          <span
            aria-hidden="true"
            className="absolute right-0 bottom-0 left-0 h-[0.8em] -skew-x-3 rounded-[2px] bg-green-300/70"
          />
          <span className="relative z-10">{title}</span>
        </h2>
        <span aria-hidden="true" className="h-px w-10 bg-slate-300" />
      </div>
    </div>
  );
}

/* ========================================================================== */
/*  Carousel                                                                  */
/* ========================================================================== */

const GRID_COLS_BY_COUNT: Record<number, string> = {
  1: '@3xl:grid-cols-1 @3xl:mx-auto @3xl:max-w-sm',
  2: '@3xl:grid-cols-2 @3xl:mx-auto @3xl:max-w-2xl',
  3: '@3xl:grid-cols-3',
};

function PricingCarousel({
  tiers,
  variant,
  trialState: individualTrialState,
  currentTier,
  onNotify,
}: {
  tiers: TierDefinition[];
  variant: 'landing' | 'dashboard';
  trialState: PricingTrialState;
  currentTier?: TierId;
  onNotify: () => void;
}) {
  const pathname = usePathname();
  const scrollRef = useRef<HTMLDivElement>(null);

  // On mobile, scroll to the user's plan card instead of the highlighted one
  const userTierId: TierId | null =
    individualTrialState.type === 'subscribed'
      ? individualTrialState.tier
      : (currentTier ?? null);

  const userTierIdx = userTierId
    ? tiers.findIndex((t) => t.id === userTierId)
    : -1;

  const highlightedIdx = tiers.findIndex((t) => t.highlighted);

  const initialIndex =
    userTierIdx !== -1
      ? userTierIdx
      : highlightedIdx !== -1
        ? highlightedIdx
        : 0;

  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.offsetWidth;
    const count = el.children.length;
    if (maxScroll <= 0) {
      setActiveIndex(initialIndex);
    } else {
      const ratio = el.scrollLeft / maxScroll;
      const closest = Math.round(ratio * (count - 1));
      setActiveIndex(closest);
    }
    setCanScrollLeft(el.scrollLeft > 8);
    setCanScrollRight(el.scrollLeft + el.offsetWidth < el.scrollWidth - 8);
  }, [initialIndex]);

  // Scroll carousel to the computed initial card on mount/update (no animation)
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const card = el.children[initialIndex] as HTMLElement | undefined;
    if (card) {
      const scrollLeft =
        card.offsetLeft -
        el.offsetLeft -
        (el.clientWidth - card.offsetWidth) / 2;
      el.scrollLeft = scrollLeft;
    }

    // Defer initial state read to avoid synchronous setState in effect body
    requestAnimationFrame(() => updateScrollState());

    const ro = new ResizeObserver(() => updateScrollState());
    ro.observe(el);
    return () => ro.disconnect();
  }, [initialIndex, updateScrollState]);

  const scrollTo = useCallback((index: number) => {
    const el = scrollRef.current;
    if (!el) return;
    const card = el.children[index] as HTMLElement | undefined;
    if (card) {
      card.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }, []);

  return (
    <div className="relative mx-auto max-w-4xl">
      {/* Scrollable container — mobile: horizontal snap; desktop: grid */}
      <div
        ref={scrollRef}
        onScroll={updateScrollState}
        className={cn(
          'scrollbar-none flex snap-x snap-mandatory items-start gap-4 overflow-x-auto px-1 pt-4 pb-5 @3xl:grid @3xl:items-start @3xl:gap-4 @3xl:overflow-visible @3xl:p-0',
          tiers.length === 1 && 'justify-center',
          GRID_COLS_BY_COUNT[tiers.length],
        )}
        style={{
          maskImage:
            canScrollLeft && canScrollRight
              ? 'linear-gradient(to right, transparent 0%, black 15%, black 85%, transparent 100%)'
              : canScrollLeft
                ? 'linear-gradient(to right, transparent 0%, black 15%)'
                : canScrollRight
                  ? 'linear-gradient(to right, black 85%, transparent 100%)'
                  : undefined,
          WebkitMaskImage:
            canScrollLeft && canScrollRight
              ? 'linear-gradient(to right, transparent 0%, black 15%, black 85%, transparent 100%)'
              : canScrollLeft
                ? 'linear-gradient(to right, transparent 0%, black 15%)'
                : canScrollRight
                  ? 'linear-gradient(to right, black 85%, transparent 100%)'
                  : undefined,
        }}
      >
        {tiers.map((tier) => {
          const isTrialTier = tier.id === TRIAL_TIER;
          const isOnTrial =
            individualTrialState.type === 'active' && isTrialTier;
          const isExpiredTrial =
            individualTrialState.type === 'expired' && isTrialTier;
          const isSubscribed =
            individualTrialState.type === 'subscribed' &&
            tier.id === individualTrialState.tier;
          const hasAnySubscription = individualTrialState.type === 'subscribed';
          const isCurrent = currentTier === tier.id;

          // Card border/shadow — suppress "Popularny" highlight when user has any subscription
          const showHighlighted =
            tier.highlighted &&
            !hasAnySubscription &&
            !isOnTrial &&
            !isExpiredTrial;
          const cardClass = cn(
            'relative flex flex-col rounded-xl border-2 border-[#d5d0c8] bg-[#fffef9] p-6 shadow-[3px_3px_0_0_#d5d0c8]',
            isSubscribed &&
              'border-green-500 shadow-[3px_3px_0_0_#22c55e] @3xl:-mt-2 @3xl:mb-[-8px] @3xl:p-7',
            isOnTrial &&
              'border-blue-500 shadow-[3px_3px_0_0_#3b82f6] @3xl:-mt-2 @3xl:mb-[-8px] @3xl:p-7',
            isExpiredTrial &&
              'border-red-400 shadow-[3px_3px_0_0_#f87171] @3xl:-mt-2 @3xl:mb-[-8px] @3xl:p-7',
            showHighlighted &&
              'border-zinc-900 shadow-[3px_3px_0_0_#303030] @3xl:-mt-2 @3xl:mb-[-8px] @3xl:p-7',
          );

          // Button
          let buttonEl: React.ReactNode = null;
          if (isSubscribed) {
            // TEMPORARY: until Stripe billing is live, show the "coming soon"
            // popup instead of linking to /home/billing.
            buttonEl = (
              <button type="button" onClick={onNotify} className={BTN_PRIMARY}>
                Zarządzaj subskrypcją
              </button>
            );
          } else if (variant === 'landing') {
            const signUpUrl =
              pathname && pathname !== '/' && !pathname.startsWith('/auth/')
                ? `/auth/sign-up?next=${encodeURIComponent(pathname)}`
                : '/auth/sign-up';

            buttonEl = (
              <Link
                href={signUpUrl}
                className={
                  tier.highlighted || isTrialTier ? BTN_PRIMARY : BTN_WARM
                }
              >
                {isTrialTier
                  ? `Wypróbuj ${TRIAL_DAYS} dni za darmo`
                  : 'Wybierz plan'}
              </Link>
            );
          } else {
            buttonEl = (
              <button
                type="button"
                onClick={onNotify}
                disabled={isCurrent}
                className={cn(
                  showHighlighted || isOnTrial || isExpiredTrial
                    ? BTN_PRIMARY
                    : BTN_WARM,
                  isCurrent && 'cursor-default opacity-50',
                )}
              >
                {isCurrent ? 'Twój obecny plan' : <>Wybierz plan</>}
              </button>
            );
          }

          const daysRemaining =
            individualTrialState.type === 'active'
              ? individualTrialState.daysRemaining
              : 0;

          return (
            <div
              key={tier.id}
              className={cn(
                cardClass,
                'w-[75vw] max-w-68 min-w-[260px] shrink-0 snap-center @3xl:w-full @3xl:max-w-none @3xl:min-w-0 @3xl:shrink',
              )}
            >
              <CardBadge
                tier={tier}
                isOnTrial={isOnTrial}
                isExpiredTrial={isExpiredTrial}
                isSubscribed={isSubscribed}
                showHighlighted={showHighlighted}
              />

              <h3 className="font-heading text-lg font-semibold text-slate-700">
                {tier.name}
              </h3>
              <p className="font-heading mt-0.5 text-xs text-slate-500 italic">
                {tier.subtitle}
              </p>

              <div className="mt-3 flex items-baseline gap-0.5">
                {tier.originalPrice && (
                  <span className="mr-1.5 text-xl font-medium text-slate-400 line-through">
                    {tier.originalPrice} zł
                  </span>
                )}
                <span
                  className={cn(
                    'text-4xl font-black',
                    tier.originalPrice ? 'text-emerald-600' : 'text-zinc-900',
                  )}
                >
                  {tier.price}
                </span>
                <span className="text-sm text-slate-500">zł/mies.</span>
              </div>

              {variant === 'landing' && isTrialTier && (
                <span className="mt-1.5 inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-medium text-emerald-700">
                  {TRIAL_DAYS} dni za darmo
                </span>
              )}

              <Features
                maxDrugs={tier.maxDrugs}
                pairs={tier.pairs}
                monthlyChecks={tier.monthlyChecks}
                highlighted={tier.highlighted}
              />
              {buttonEl}

              <CardStripe
                isOnTrial={isOnTrial}
                isExpiredTrial={isExpiredTrial}
                isSubscribed={isSubscribed}
                isLandingTrial={variant === 'landing' && isTrialTier}
                daysRemaining={daysRemaining}
              />
            </div>
          );
        })}
      </div>

      {/* Dot indicators — mobile only */}
      <div
        className={cn(
          'mt-4 flex justify-center gap-2 @3xl:hidden',
          tiers.length < 2 && 'hidden',
        )}
      >
        {tiers.map((tier, i) => (
          <button
            key={tier.id}
            type="button"
            aria-label={`Plan ${tier.name}`}
            onClick={() => scrollTo(i)}
            className={cn(
              'h-2 w-2 rounded-full border border-[#d5d0c8] transition-colors',
              i === activeIndex ? 'bg-zinc-900' : 'bg-[#eceae4]',
            )}
          />
        ))}
      </div>
    </div>
  );
}
